import { HttpApiService } from "@/shared/services/httpApi.service";
import { readApiCode } from "@/shared/utils/apiError";

export type BrebKeySource = "DOCUMENT" | "PHONE" | "GENERATED";
/** CUSTOM keys are assigned by allowlisted staff; an organization never picks one as an option. */
export type BrebReceivingKeySource = BrebKeySource | "CUSTOM";
export type BrebKeyAvailability = "AVAILABLE" | "TAKEN" | "UNKNOWN";
export type BrebKeyStatus = "PENDING" | "ACTIVE" | "CANCELLED";

export interface BrebReceivingKey {
  id: string;
  key_value: string;
  key_type: string;
  source: BrebReceivingKeySource;
  status: BrebKeyStatus;
  /** The organization's primary key: amount-specific QRs are minted over it unless the payin names another. */
  is_primary: boolean;
  holder_name: string;
  created_at: string;
  activated_at: string | null;
  /** COP decimal strings of the bounds the payer's bank enforces on the key; null while not applied yet. */
  min_amount: string | null;
  max_amount: string | null;
  /** EMV of the organization's any-amount QR: ACTIVE keys only, null while it is being generated. */
  static_qr_payload: string | null;
}

export interface BrebKeyOption {
  source: BrebKeySource;
  /** PHONE is the mobile from the organization's approved verification, offered only when it is Colombian. */
  key_value: string;
  /** Always false: no option takes a value from the user. */
  editable: boolean;
  availability: BrebKeyAvailability | null;
  /** Another Coloca organization holds the value; false when it is free, unknown or held outside Coloca. */
  taken_in_coloca: boolean;
}

export type BrebKeyFailureReason = "key_taken" | "key_rejected" | "provider_rejected";

export interface BrebKeyLastFailure {
  key_value: string;
  source?: BrebReceivingKeySource;
  reason: BrebKeyFailureReason;
  failed_at: string;
}

export interface BrebReceivingKeyState {
  /** The primary key, or null without keys. */
  key: BrebReceivingKey | null;
  /** Every live key, primary first. */
  keys: BrebReceivingKey[];
  /** `enabled`: the organization is inside the key rollout (switched by staff, or open to all). */
  eligibility: { enabled: boolean; verified: boolean; rail_enabled: boolean };
  /** Read against the directory only without keys, or when asked for with `includeOptions`. */
  options: BrebKeyOption[];
  /** The options the organization can still add, known without asking the directory. */
  addable_sources: BrebKeySource[];
  /** Only while no live key of that source exists and its newest attempt of the last 24 h failed. */
  last_failure: BrebKeyLastFailure | null;
}

type BrebReceivingKeyWire = Omit<BrebReceivingKey, "id" | "is_primary"> &
  Partial<Pick<BrebReceivingKey, "id" | "is_primary">>;

/** A backend that predates multiple keys sends only `key`. */
export type BrebReceivingKeyStateResponse = Omit<
  BrebReceivingKeyState,
  "key" | "keys" | "addable_sources"
> & {
  key: BrebReceivingKeyWire | null;
  keys?: BrebReceivingKeyWire[];
  addable_sources?: BrebKeySource[];
};

export interface BrebCustomKeyAvailability {
  /** The value as the backend normalized it. */
  key_value: string;
  availability: BrebKeyAvailability;
  /** A Coloca organization holds it; false when free, unknown or held at another Bre-B entity. */
  taken_in_coloca: boolean;
}

export type BrebKeyTransferProblem = "verification_required" | "rail_disabled" | "no_pricing";

/** What moving a key to another organization would do; reading it changes nothing. */
export interface BrebKeyTransferPlan {
  key: BrebReceivingKey;
  /** CUSTOM unless the value is also the destination's own document or mobile option. */
  arrives_as: BrebReceivingKeySource;
  /** QRs with an amount the origin can still be paid through; they keep crediting the origin. */
  live_qrs: number;
  /** Null: the origin's new QRs fall back to Coloca's shared key. */
  /** `open_sales` / `open_purchases`: USDT operations still paying to the key; the transfer refuses them. */
  origin: {
    organization_id: string;
    primary_key_value: string | null;
    open_sales?: number;
    open_purchases?: number;
  };
  /** `problem` is why the destination cannot receive the key; the transfer refuses it. */
  destination: {
    organization_id: string;
    name: string;
    primary_key_value: string;
    problem: BrebKeyTransferProblem | null;
  };
}

export interface BrebKeyTransferResult {
  /** The key as the destination holds it now: a new id, the same value and static QR. */
  key: BrebReceivingKey;
  from_organization_id: string;
  to_organization_id: string;
  live_qrs: number;
}

export interface BrebKeyHolderNoticeResult {
  /** False when the holder was already told in the last 24 h or is not a Coloca organization. */
  notified: boolean;
}

const BREB_KEY_URL = "/internal/payments/breb-receiving-key";
const TWO_FACTOR_HEADER = "x-two-factor-code";

const orgQuery = (organizationId?: string): Record<string, string> =>
  organizationId ? { organization_id: organizationId } : {};

const twoFactorHeaders = (code?: string) => (code ? { headers: { [TWO_FACTOR_HEADER]: code } } : {});

// Same rule the backend applies: lowercase, trimmed, one leading @, then 5–20 letters or digits.
const BREB_ALPHANUM_KEY_PATTERN = /^@[a-z0-9]{5,20}$/;

export const normalizeCustomBrebKey = (raw: string): string | null => {
  const keyValue = `@${raw.trim().toLowerCase().replace(/^@+/, "")}`;
  return BREB_ALPHANUM_KEY_PATTERN.test(keyValue) ? keyValue : null;
};

const LEGACY_KEY_ID_PREFIX = "legacy:";

const isIssuedKeyId = (keyId: string): boolean => !keyId.startsWith(LEGACY_KEY_ID_PREFIX);

const normalizeKey = (key: BrebReceivingKeyWire, primary: boolean): BrebReceivingKey => ({
  ...key,
  id: key.id ?? `${LEGACY_KEY_ID_PREFIX}${key.key_value}`,
  is_primary: key.is_primary ?? (primary && key.status === "ACTIVE"),
});

export const normalizeBrebKeyState = (
  state: BrebReceivingKeyStateResponse | BrebReceivingKeyState,
): BrebReceivingKeyState => {
  const wireKeys = state.keys ?? (state.key ? [state.key] : []);
  const keys = wireKeys.map((key, index) => normalizeKey(key, index === 0));
  return {
    ...state,
    key: keys[0] ?? null,
    keys,
    addable_sources:
      state.addable_sources ?? (keys.length === 0 ? state.options.map((option) => option.source) : []),
  };
};

export interface RemoveBrebKeyInput {
  organizationId?: string;
  /** Without it the backend deletes the primary key. */
  keyId?: string;
  acceptLiveQrs?: boolean;
  /** Only when the backend asked for it: users without 2FA delete without one. */
  twoFactorCode?: string;
}

export const BrebReceivingKeyService = {
  /** `includeOptions` asks the directory about the options the organization can still add. */
  async getState(organizationId?: string, includeOptions = false): Promise<BrebReceivingKeyState> {
    const { data } = await HttpApiService.get<BrebReceivingKeyStateResponse>(BREB_KEY_URL, {
      params: { ...orgQuery(organizationId), ...(includeOptions ? { include_options: "true" } : {}) },
    });
    return normalizeBrebKeyState(data);
  },

  async create(source: BrebKeySource, organizationId?: string): Promise<BrebReceivingKey> {
    const { data } = await HttpApiService.post<BrebReceivingKey>(BREB_KEY_URL, {
      source,
      ...orgQuery(organizationId),
    });
    return data;
  },

  async remove({
    organizationId,
    keyId,
    acceptLiveQrs = false,
    twoFactorCode,
  }: RemoveBrebKeyInput = {}): Promise<BrebReceivingKey> {
    const { data } = await HttpApiService.delete<BrebReceivingKey>(BREB_KEY_URL, {
      params: {
        ...(keyId && isIssuedKeyId(keyId) ? { key_id: keyId } : {}),
        ...orgQuery(organizationId),
        ...(acceptLiveQrs ? { accept_live_qrs: "true" } : {}),
      },
      ...twoFactorHeaders(twoFactorCode),
    });
    return data;
  },

  async setPrimary(keyId: string, organizationId?: string): Promise<BrebReceivingKey> {
    const { data } = await HttpApiService.patch<BrebReceivingKey>(
      `${BREB_KEY_URL}/${encodeURIComponent(keyId)}/primary`,
      {},
      { params: orgQuery(organizationId) },
    );
    return data;
  },

  async getCustomAccess(): Promise<boolean> {
    const { data } = await HttpApiService.get<{ allowed: boolean }>(`${BREB_KEY_URL}/custom/access`);
    return data.allowed;
  },

  async checkCustomAvailability(value: string): Promise<BrebCustomKeyAvailability> {
    const { data } = await HttpApiService.get<BrebCustomKeyAvailability>(
      `${BREB_KEY_URL}/custom/availability`,
      {
        params: { value },
      },
    );
    return data;
  },

  async assignCustom(
    organizationId: string,
    keyValue: string,
    twoFactorCode: string,
  ): Promise<BrebReceivingKey> {
    const { data } = await HttpApiService.post<BrebReceivingKey>(
      `${BREB_KEY_URL}/custom`,
      { organization_id: organizationId, key_value: keyValue },
      twoFactorHeaders(twoFactorCode),
    );
    return data;
  },

  async getTransferPlan(
    keyId: string,
    fromOrganizationId: string,
    toOrganizationId: string,
  ): Promise<BrebKeyTransferPlan> {
    const { data } = await HttpApiService.get<BrebKeyTransferPlan>(
      `${BREB_KEY_URL}/${encodeURIComponent(keyId)}/transfer`,
      { params: { from_organization_id: fromOrganizationId, to_organization_id: toOrganizationId } },
    );
    return data;
  },

  async transferKey(
    keyId: string,
    fromOrganizationId: string,
    toOrganizationId: string,
    twoFactorCode: string,
  ): Promise<BrebKeyTransferResult> {
    const { data } = await HttpApiService.post<BrebKeyTransferResult>(
      `${BREB_KEY_URL}/${encodeURIComponent(keyId)}/transfer`,
      { from_organization_id: fromOrganizationId, to_organization_id: toOrganizationId },
      twoFactorHeaders(twoFactorCode),
    );
    return data;
  },

  async notifyHolder(source: BrebKeySource, organizationId?: string): Promise<BrebKeyHolderNoticeResult> {
    const { data } = await HttpApiService.post<BrebKeyHolderNoticeResult>(`${BREB_KEY_URL}/holder-notice`, {
      source,
      ...orgQuery(organizationId),
    });
    return data;
  },
};

// A 502 is upstream trouble with nothing wrong in the value, so it stays retryable.
const REFUSED_CREATE_STATUSES = new Set([409, 422]);

// The only conflict a delete answers: QRs with an amount over the key are still payable.
// A sale paying its COP to the key, or a purchase that would refund to it, also answers 409, but nothing
// overrides those, so they are not offered.
const NOT_OVERRIDABLE_CODES = new Set(["breb_key_open_sales", "breb_key_open_purchases"]);

export const isBrebKeyDeleteBlockedByLiveQrs = (error: unknown): boolean =>
  HttpApiService.isAxiosError(error) &&
  error.response?.status === 409 &&
  !NOT_OVERRIDABLE_CODES.has(readApiCode(error) ?? "");

export const isBrebKeyCreateRefused = (error: unknown): boolean =>
  HttpApiService.isAxiosError(error) && REFUSED_CREATE_STATUSES.has(error.response?.status ?? 0);

const activationTime = (key: BrebReceivingKey): number =>
  key.activated_at ? Date.parse(key.activated_at) : Number.POSITIVE_INFINITY;

// The backend's order once no key is pinned: ACTIVE first, then by activation and creation.
const byActivation = (a: BrebReceivingKey, b: BrebReceivingKey): number =>
  Number(a.status !== "ACTIVE") - Number(b.status !== "ACTIVE") ||
  activationTime(a) - activationTime(b) ||
  Date.parse(a.created_at) - Date.parse(b.created_at) ||
  a.id.localeCompare(b.id);

// Only an ACTIVE key can be the one QRs are minted over.
const withPrimaryFlag = (keys: BrebReceivingKey[]): BrebReceivingKey[] =>
  keys.map((key, index) => ({ ...key, is_primary: index === 0 && key.status === "ACTIVE" }));

// A new ACTIVE key was activated last, so it follows the ACTIVE ones; a PENDING one goes to the end.
export const seedCreatedKeyState = (
  previous: BrebReceivingKeyState | null,
  created: BrebReceivingKey,
): BrebReceivingKeyState => {
  const others = (previous?.keys ?? []).filter((key) => key.id !== created.id);
  const active = others.filter((key) => key.status === "ACTIVE");
  const pending = others.filter((key) => key.status !== "ACTIVE");
  const keys = withPrimaryFlag(
    created.status === "ACTIVE" ? [...active, created, ...pending] : [...active, ...pending, created],
  );
  return {
    eligibility: { enabled: true, verified: true, rail_enabled: true },
    ...previous,
    key: keys[0],
    keys,
    options: [],
    addable_sources: (previous?.addable_sources ?? []).filter((source) => source !== created.source),
    last_failure: null,
  };
};

export const seedRemovedKeyState = (
  previous: BrebReceivingKeyState,
  removedId: string,
): BrebReceivingKeyState => {
  const keys = withPrimaryFlag(previous.keys.filter((key) => key.id !== removedId));
  return { ...previous, key: keys[0] ?? null, keys };
};

// The pinned key leads; the others are unpinned, so they follow in activation order.
export const seedPrimaryKeyState = (
  previous: BrebReceivingKeyState,
  primaryId: string,
): BrebReceivingKeyState => {
  const primary = previous.keys.find((key) => key.id === primaryId);
  if (!primary) return previous;
  const rest = previous.keys.filter((key) => key.id !== primaryId).sort(byActivation);
  const keys = withPrimaryFlag([primary, ...rest]);
  return { ...previous, key: keys[0], keys };
};

export type BrebKeyDeleteOutcome = "last" | "primary" | "secondary";

export interface BrebKeyDeleteImpact {
  outcome: BrebKeyDeleteOutcome;
  /** The key that becomes the primary, when the deleted one was it and an ACTIVE one remains. */
  successorValue?: string;
}

// What new QRs with an amount use once the key is gone: the next ACTIVE key, the same primary, or the shared key.
export const brebKeyDeleteImpact = (
  keys: readonly BrebReceivingKey[],
  keyId: string,
): BrebKeyDeleteImpact => {
  const target = keys.find((key) => key.id === keyId);
  if (target?.is_primary) {
    const next = keys.find((key) => key.id !== keyId && key.status === "ACTIVE");
    return next ? { outcome: "primary", successorValue: next.key_value } : { outcome: "last" };
  }
  return keys.some((key) => key.is_primary) ? { outcome: "secondary" } : { outcome: "last" };
};

export const activeBrebKeys = (state: BrebReceivingKeyState | null): BrebReceivingKey[] =>
  state?.keys.filter((key) => key.status === "ACTIVE") ?? [];

export interface BrebKeyPollTier {
  intervalMs: number;
  maxAttempts: number;
}

export interface BrebKeyPollPlan extends BrebKeyPollTier {
  /** Changes with the keys, their status and their QRs, so each new wait gets a fresh budget. */
  id: string;
  /** Slower tiers taken in turn once this one runs out. */
  next: BrebKeyPollTier[];
}

const PENDING_TIER: BrebKeyPollTier = { intervalMs: 4000, maxAttempts: 30 };
// A missing static QR is retried by the backend sweep every 5 min, so its poll outlasts two sweeps.
const MISSING_QR_TIER: BrebKeyPollTier = { intervalMs: 15_000, maxAttempts: 60 };
const STEADY_TIER: BrebKeyPollTier = { intervalMs: 120_000, maxAttempts: Number.POSITIVE_INFINITY };

// The most urgent key sets the pace; once its budget is spent the slower tiers still run, ending on the steady one.
export const brebKeyPollPlan = (keys: readonly BrebReceivingKey[]): BrebKeyPollPlan | null => {
  const live = keys.filter((key) => key.status === "PENDING" || key.status === "ACTIVE");
  if (live.length === 0) return null;
  const id = live.map((key) => `${key.key_value}:${key.status}:${!!key.static_qr_payload}`).join("|");
  const tiers = [
    ...(live.some((key) => key.status === "PENDING") ? [PENDING_TIER] : []),
    ...(live.some((key) => key.status === "ACTIVE" && !key.static_qr_payload) ? [MISSING_QR_TIER] : []),
    STEADY_TIER,
  ];
  const [first, ...next] = tiers;
  return { id, ...first, next };
};

export interface BrebKeyPollCursor {
  /** 0 is the plan's own tier, then `next` in order. */
  tier: number;
  count: number;
}

export const brebKeyPollTier = (plan: BrebKeyPollPlan, cursor: BrebKeyPollCursor): BrebKeyPollTier =>
  cursor.tier === 0 ? plan : plan.next[cursor.tier - 1];

// One more attempt spent: stay on the tier, move to the next one with a fresh budget, or stop after the last.
export const advanceBrebKeyPoll = (
  plan: BrebKeyPollPlan,
  cursor: BrebKeyPollCursor,
): BrebKeyPollCursor | null => {
  const count = cursor.count + 1;
  if (count < brebKeyPollTier(plan, cursor).maxAttempts) return { tier: cursor.tier, count };
  return cursor.tier < plan.next.length ? { tier: cursor.tier + 1, count: 0 } : null;
};
